import React, { useState, useEffect } from 'react';
import { CrossLandingPage, CrossLandingPageFormData } from './types';
import PolymorphicSelector from './PolymorphicSelector';

interface CrossLandingPageFormProps {
  crossLandingPage: CrossLandingPage | null;
  apiBaseUrl: string;
  csrfToken: string;
  onSave: () => void;
  onCancel: () => void;
}

const CrossLandingPageForm: React.FC<CrossLandingPageFormProps> = ({
  crossLandingPage,
  apiBaseUrl,
  csrfToken,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState<CrossLandingPageFormData>({
    primary_type: 'Skill',
    primary_id: null,
    secondary_type: 'Occupation',
    secondary_id: null,
    slug: '',
    published: false,
    display_priority: 100,
    search_params: {},
  });

  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdditionalParams, setShowAdditionalParams] = useState(false);

  useEffect(() => {
    if (crossLandingPage) {
      setFormData({
        primary_type: crossLandingPage.primary_type,
        primary_id: crossLandingPage.primary_id,
        secondary_type: crossLandingPage.secondary_type,
        secondary_id: crossLandingPage.secondary_id,
        slug: crossLandingPage.slug || '',
        published: crossLandingPage.published,
        display_priority: crossLandingPage.display_priority,
        search_params: crossLandingPage.search_params || {},
      });
    }
  }, [crossLandingPage]);

  const generateSlug = () => {
    // slugはモデル側で自動生成されるため、ここでは何もしない
    // 必要に応じて手動でslugを設定可能
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const url = crossLandingPage
        ? `${apiBaseUrl}/${crossLandingPage.id}`
        : apiBaseUrl;
      const method = crossLandingPage ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          cross_landing_page: formData,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(crossLandingPage ? '更新しました' : '作成しました');
        onSave();
      } else {
        setErrors(result.errors || {});
        alert('保存に失敗しました');
      }
    } catch (error) {
      console.error('Submit failed:', error);
      alert('保存に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* 基本情報 */}
      <div className="mb-4">
        <h6 className="mb-3">基本情報</h6>

        <div className="mb-3">
          <label className="form-label">slug（任意）</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={generateSlug}
            >
              自動生成
            </button>
          </div>
        </div>

        <div className="mb-3">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              checked={formData.published}
              onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
            />
            <label className="form-check-label">公開する</label>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">表示優先度</label>
          <input
            type="number"
            className="form-control"
            value={formData.display_priority}
            onChange={(e) =>
              setFormData({ ...formData, display_priority: parseInt(e.target.value, 10) || 100 })
            }
          />
        </div>
      </div>

      {/* かけ合わせ条件 */}
      <div className="mb-4">
        <h6 className="mb-3">かけ合わせ条件</h6>

        <div className="mb-3">
          <label className="form-label">
            Primary Type <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            value={formData.primary_type}
            onChange={(e) =>
              setFormData({
                ...formData,
                primary_type: e.target.value as 'Skill' | 'Occupation' | 'Characteristic',
                primary_id: null,
              })
            }
          >
            <option value="Skill">Skill</option>
            <option value="Occupation">Occupation</option>
            <option value="Characteristic">Characteristic</option>
          </select>
        </div>

        <PolymorphicSelector
          type={formData.primary_type}
          value={formData.primary_id}
          onChange={(id) => setFormData({ ...formData, primary_id: id })}
          apiBaseUrl={apiBaseUrl}
          csrfToken={csrfToken}
          label="Primary"
          required
          error={errors.primary_id}
        />

        <div className="mb-3">
          <label className="form-label">
            Secondary Type <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            value={formData.secondary_type}
            onChange={(e) =>
              setFormData({
                ...formData,
                secondary_type: e.target.value as 'Skill' | 'Occupation' | 'Characteristic',
                secondary_id: null,
              })
            }
          >
            <option value="Skill">Skill</option>
            <option value="Occupation">Occupation</option>
            <option value="Characteristic">Characteristic</option>
          </select>
        </div>

        <PolymorphicSelector
          type={formData.secondary_type}
          value={formData.secondary_id}
          onChange={(id) => setFormData({ ...formData, secondary_id: id })}
          apiBaseUrl={apiBaseUrl}
          csrfToken={csrfToken}
          label="Secondary"
          required
          error={errors.secondary_id}
        />
      </div>

      {/* 追加検索条件 */}
      <div className="mb-4">
        <button
          type="button"
          className="btn btn-link p-0 mb-2"
          onClick={() => setShowAdditionalParams(!showAdditionalParams)}
        >
          {showAdditionalParams ? '▼' : '▶'} 追加検索条件（任意）
        </button>

        {showAdditionalParams && (
          <div className="border rounded p-3">
            <div className="mb-3">
              <label className="form-label">リモートタイプ</label>
              <div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.search_params.remote_types?.includes('remote') || false}
                    onChange={(e) => {
                      const remoteTypes = formData.search_params.remote_types || [];
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          search_params: {
                            ...formData.search_params,
                            remote_types: [...remoteTypes, 'remote'],
                          },
                        });
                      } else {
                        setFormData({
                          ...formData,
                          search_params: {
                            ...formData.search_params,
                            remote_types: remoteTypes.filter((t) => t !== 'remote'),
                          },
                        });
                      }
                    }}
                  />
                  <label className="form-check-label">remote</label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.search_params.remote_types?.includes('full_remote') || false}
                    onChange={(e) => {
                      const remoteTypes = formData.search_params.remote_types || [];
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          search_params: {
                            ...formData.search_params,
                            remote_types: [...remoteTypes, 'full_remote'],
                          },
                        });
                      } else {
                        setFormData({
                          ...formData,
                          search_params: {
                            ...formData.search_params,
                            remote_types: remoteTypes.filter((t) => t !== 'full_remote'),
                          },
                        });
                      }
                    }}
                  />
                  <label className="form-check-label">full_remote</label>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">報酬下限</label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  value={formData.search_params.price_over || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      search_params: {
                        ...formData.search_params,
                        price_over: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      },
                    })
                  }
                />
                <span className="input-group-text">円/月</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* プレビュー */}
      <div className="mb-4">
        <h6 className="mb-3">プレビュー</h6>
        <pre className="bg-light p-3 rounded" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
          {JSON.stringify(
            {
              skill_ids: formData.primary_type === 'Skill' ? [formData.primary_id] : formData.secondary_type === 'Skill' ? [formData.secondary_id] : [],
              occupation_ids: formData.primary_type === 'Occupation' ? [formData.primary_id] : formData.secondary_type === 'Occupation' ? [formData.secondary_id] : [],
              characteristic_ids: formData.primary_type === 'Characteristic' ? [formData.primary_id] : formData.secondary_type === 'Characteristic' ? [formData.secondary_id] : [],
              ...formData.search_params,
            },
            null,
            2
          )}
        </pre>
      </div>

      {/* ボタン */}
      <div className="d-flex justify-content-end gap-2">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isSubmitting}>
          キャンセル
        </button>
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? '保存中...' : crossLandingPage ? '更新' : '作成'}
        </button>
      </div>
    </form>
  );
};

export default CrossLandingPageForm;

